import data from "./data/locations.json";

// Vendored Danish location dataset (DAWA / Dataforsyningen), spanning three
// administrative tiers. Regenerate with `node scripts/generate-locations.mjs`.
//
// A location is referenced by a typed token: `${type}:${code}`, e.g.
// "region:1081", "kommune:0851", "postnummer:9000".
export type LocationType = "land" | "region" | "kommune" | "postnummer";

export interface DkLocation {
  token: string;
  type: LocationType;
  navn: string;
  lat: number;
  lon: number;
}

interface RawEntry {
  type: LocationType;
  navn: string;
  lat: number;
  lon: number;
}

const RAW: Record<string, RawEntry> = data as Record<string, RawEntry>;

const ALL: DkLocation[] = Object.entries(RAW)
  .map(([token, e]) => ({ token, type: e.type, navn: e.navn, lat: e.lat, lon: e.lon }))
  .sort((a, b) => a.token.localeCompare(b.token));

// Broad areas first so aggregate options surface above individual postal codes.
const TYPE_RANK: Record<LocationType, number> = { land: 0, region: 1, kommune: 2, postnummer: 3 };

// Extra search terms for the nationwide option, which has no place name to
// match. Matched by word-prefix (not substring) so a query like "ar" — which
// appears inside "danmark" — doesn't spuriously surface "Hele Danmark".
const NATIONWIDE_ALIASES = ["danmark", "hele", "landsdækkende", "landsdaekkende", "land", "dk"];

/** Human label, e.g. "Hele Danmark", "Region Nordjylland", "Aalborg Kommune", "9000 Aalborg". */
export function locationLabel(loc: Pick<DkLocation, "type" | "navn"> & { code?: string; token?: string }): string {
  const code = loc.code ?? loc.token?.split(":")[1] ?? "";
  switch (loc.type) {
    case "land":
      return "Hele Danmark";
    case "region":
      return loc.navn; // DAWA name already includes the "Region " prefix
    case "kommune":
      return `${loc.navn} Kommune`;
    case "postnummer":
      return `${code} ${loc.navn}`;
  }
}

/** Short Danish label for the tier, used as a badge in the picker. */
export function locationTypeLabel(type: LocationType): string {
  switch (type) {
    case "land":
      return "Hele landet";
    case "region":
      return "Region";
    case "kommune":
      return "Kommune";
    case "postnummer":
      return "Postnr.";
  }
}

/** Resolve a stored token to its full record (coordinates + name), or null. */
export function lookupLocation(token: string | null | undefined): DkLocation | null {
  if (!token) return null;
  const e = RAW[token];
  return e ? { token, type: e.type, navn: e.navn, lat: e.lat, lon: e.lon } : null;
}

/**
 * Search across all tiers. Numeric queries match postal codes by prefix;
 * text queries match place names. Broader tiers (region, kommune) rank first so
 * aggregate areas are easy to pick.
 */
export function searchLocations(term: string, limit = 8): DkLocation[] {
  const q = term.trim().toLowerCase();
  if (!q) return [];

  const result = /^\d+$/.test(q)
    ? // Numeric query → postal codes by code prefix.
      ALL.filter((l) => l.type === "postnummer" && l.token.split(":")[1].startsWith(q))
    : // Text query → place names across all tiers (nationwide matches aliases).
      ALL.filter((l) =>
        l.type === "land"
          ? NATIONWIDE_ALIASES.some((w) => w.startsWith(q))
          : l.navn.toLowerCase().includes(q),
      );

  return result
    .sort((a, b) => {
      const byType = TYPE_RANK[a.type] - TYPE_RANK[b.type];
      if (byType !== 0) return byType;
      return a.navn.localeCompare(b.navn, "da");
    })
    .slice(0, limit);
}
